import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { useWorkspaceStore } from '@/store/workspaceStore'
import {
  ChevronDown,
  ChevronRight,
  MapPin,
  Box,
  Code,
  ArrowRightLeft,
  Leaf,
  Package,
  Database,
  Plus,
  Trash2,
  Pencil,
  Loader2,
} from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import { projectAPI } from '@/lib/api/projects'
import { getApiErrorMessage } from '@/lib/api/errors'
import { cn } from '@/lib/utils'
import type { ActivityWithExchanges, ProjectExchange } from '@/lib/types/project-graph'
import { AddExchangeDialog } from './AddExchangeDialog'
import { PropertiesEditor } from './PropertiesEditor'

interface ActivityDetailViewProps {
  activity: ActivityWithExchanges
  projectDatabase?: string
  allActivities: ActivityWithExchanges[]
  onExchangeCreated?: () => void
}

export function ActivityDetailView({
  activity,
  projectDatabase,
  allActivities,
  onExchangeCreated,
}: ActivityDetailViewProps) {
  const [exchangeDialogOpen, setExchangeDialogOpen] = useState(false)
  const [exchangeDialogType, setExchangeDialogType] = useState<'technosphere' | 'biosphere'>(
    'technosphere',
  )

  const openAddExchange = (type: 'technosphere' | 'biosphere') => {
    setExchangeDialogType(type)
    setExchangeDialogOpen(true)
  }

  const handleDeleteActivity = async () => {
    try {
      await projectAPI.deleteActivity(activity.id)
      toast.success(`Activity "${activity.name}" deleted`)
      onExchangeCreated?.()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to delete activity'))
    }
  }

  const handleDeleteExchange = async (exchangeId: string, exchangeName: string) => {
    try {
      await projectAPI.deleteExchange(activity.id, exchangeId)
      toast.success(`Exchange "${exchangeName}" deleted`)
      onExchangeCreated?.()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to delete exchange'))
    }
  }

  const handleEditExchange = async (
    exchangeId: string,
    patch: { amount?: number; name?: string },
  ) => {
    await projectAPI.updateExchange(activity.id, exchangeId, patch)
    toast.success('Exchange updated')
    onExchangeCreated?.()
  }

  // Group exchanges by type
  const productionExchanges = activity.exchanges.filter((e) => e.exchange_type === 'production')
  const technosphereExchanges = activity.exchanges.filter((e) => e.exchange_type === 'technosphere')
  const biosphereExchanges = activity.exchanges.filter((e) => e.exchange_type === 'biosphere')

  // Further split technosphere by input type
  const technosphereFromDb = technosphereExchanges.filter(
    (e) =>
      e.input_database && !e.input_database.startsWith('project:') && e.input_database !== 'custom',
  )
  const technosphereCustom = technosphereExchanges.filter(
    (e) => e.input_database === 'custom' || e.input_database?.startsWith('project:'),
  )

  // Split biosphere into inputs (from nature) and outputs (emissions)
  const biosphereInputs = biosphereExchanges.filter((e) => e.amount < 0)
  const biosphereOutputs = biosphereExchanges.filter((e) => e.amount >= 0)

  return (
    <div className="divide-y divide-border">
      {/* Activity Header */}
      <div className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h4 className="text-sm font-semibold">{activity.name}</h4>
            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Code className="h-3 w-3" />
                {activity.code}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {activity.location || 'GLO'}
              </span>
              <span className="flex items-center gap-1">
                <Box className="h-3 w-3" />
                {activity.unit}
              </span>
            </div>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-destructive shrink-0"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-sm">Delete Activity</AlertDialogTitle>
                <AlertDialogDescription className="text-xs">
                  Delete "{activity.name}" and all its exchanges? This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="h-8 text-xs">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="h-8 text-xs bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={handleDeleteActivity}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Properties Editor */}
      <PropertiesEditor activity={activity} onUpdated={onExchangeCreated} />

      {/* PRODUCT OUTPUT (Production exchanges) */}
      <CollapsibleSection
        title="Product Output"
        icon={Package}
        iconColor="text-amber-500"
        count={productionExchanges.length}
        defaultOpen={true}
      >
        {productionExchanges.length === 0 ? (
          <EmptyState message="No production output defined" />
        ) : (
          <ExchangeTable exchanges={productionExchanges} showSource={false} />
        )}
      </CollapsibleSection>

      {/* INPUTS */}
      <CollapsibleSection
        title="Inputs"
        icon={ArrowRightLeft}
        iconColor="text-blue-500"
        count={technosphereExchanges.length + biosphereInputs.length}
        defaultOpen={true}
        onAdd={() => openAddExchange('technosphere')}
      >
        {/* Technosphere from databases */}
        {technosphereFromDb.length > 0 && (
          <ExchangeSubgroup
            title="From Ecoinvent"
            exchanges={technosphereFromDb}
            showSource={true}
            onDeleteExchange={handleDeleteExchange}
            onEditExchange={handleEditExchange}
          />
        )}

        {/* Technosphere from custom/project */}
        {technosphereCustom.length > 0 && (
          <ExchangeSubgroup
            title="From Project"
            exchanges={technosphereCustom}
            showSource={true}
            onDeleteExchange={handleDeleteExchange}
            onEditExchange={handleEditExchange}
          />
        )}

        {/* Biosphere inputs (from nature) */}
        {biosphereInputs.length > 0 && (
          <ExchangeSubgroup
            title="From Nature"
            exchanges={biosphereInputs}
            showSource={false}
            onDeleteExchange={handleDeleteExchange}
            onEditExchange={handleEditExchange}
          />
        )}

        {technosphereExchanges.length === 0 && biosphereInputs.length === 0 && (
          <EmptyState message="No inputs defined" onAdd={() => openAddExchange('technosphere')} />
        )}
      </CollapsibleSection>

      {/* OUTPUTS (Emissions) */}
      <CollapsibleSection
        title="Outputs / Emissions"
        icon={Leaf}
        iconColor="text-green-500"
        count={biosphereOutputs.length}
        defaultOpen={true}
        onAdd={() => openAddExchange('biosphere')}
      >
        {biosphereOutputs.length === 0 ? (
          <EmptyState message="No emissions defined" onAdd={() => openAddExchange('biosphere')} />
        ) : (
          <>
            <ExchangeTable
              exchanges={biosphereOutputs}
              showSource={false}
              onDeleteExchange={handleDeleteExchange}
              onEditExchange={handleEditExchange}
            />
          </>
        )}
      </CollapsibleSection>

      {/* Add Exchange Dialog */}
      <AddExchangeDialog
        open={exchangeDialogOpen}
        onOpenChange={setExchangeDialogOpen}
        activityId={activity.id}
        activityName={activity.name}
        projectDatabase={projectDatabase}
        projectActivities={allActivities}
        exchangeType={exchangeDialogType}
        onCreated={() => onExchangeCreated?.()}
      />
    </div>
  )
}

// Collapsible Section Component
interface CollapsibleSectionProps {
  title: string
  icon: typeof Package
  iconColor: string
  count: number
  defaultOpen?: boolean
  children: React.ReactNode
  onAdd?: () => void
}

function CollapsibleSection({
  title,
  icon: Icon,
  iconColor,
  count,
  defaultOpen = false,
  children,
  onAdd,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="group">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setIsOpen(!isOpen)
        }}
        className="flex items-center justify-between w-full px-3 py-2 hover:bg-accent/50 transition-colors cursor-pointer select-none"
      >
        <div className="flex items-center gap-2">
          {isOpen ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          <Icon className={cn('h-4 w-4', iconColor)} />
          <span className="text-xs font-medium">{title}</span>
          <Badge variant="secondary" className="text-[10px] h-4 px-1.5">
            {count}
          </Badge>
        </div>
        {onAdd && (
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 opacity-40 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation()
              onAdd()
            }}
          >
            <Plus className="h-3 w-3" />
          </Button>
        )}
      </div>

      {isOpen && <div className="px-3 pb-3">{children}</div>}
    </div>
  )
}

// Exchange Subgroup (for nested categories within Inputs)
interface ExchangeSubgroupProps {
  title: string
  exchanges: ProjectExchange[]
  showSource: boolean
  onDeleteExchange?: (exchangeId: string, exchangeName: string) => void
  onEditExchange?: (exchangeId: string, patch: { amount?: number; name?: string }) => Promise<void>
}

function ExchangeSubgroup({
  title,
  exchanges,
  showSource,
  onDeleteExchange,
  onEditExchange,
}: ExchangeSubgroupProps) {
  return (
    <div className="mb-3">
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-1.5 ml-1">
        {title} ({exchanges.length})
      </p>
      <ExchangeTable
        exchanges={exchanges}
        showSource={showSource}
        onDeleteExchange={onDeleteExchange}
        onEditExchange={onEditExchange}
      />
    </div>
  )
}

// Exchange Table Component
interface ExchangeTableProps {
  exchanges: ProjectExchange[]
  showSource: boolean
  onDeleteExchange?: (exchangeId: string, exchangeName: string) => void
  onEditExchange?: (exchangeId: string, patch: { amount?: number; name?: string }) => Promise<void>
}

function ExchangeTable({
  exchanges,
  showSource,
  onDeleteExchange,
  onEditExchange,
}: ExchangeTableProps) {
  const { selectedNodeId, selectNode, setRightTab } = useWorkspaceStore()
  const [editing, setEditing] = useState<ProjectExchange | null>(null)

  const handleExchangeClick = (exchange: ProjectExchange) => {
    selectNode(exchange.id, 'exchange')

    // If it's an Ecoinvent exchange, switch to Background tab
    if (
      exchange.input_database &&
      !exchange.input_database.startsWith('project:') &&
      exchange.input_database !== 'custom'
    ) {
      setRightTab('background')
    }
  }

  return (
    <div className="rounded-md border border-border overflow-hidden">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-muted/50">
            <th className="text-left px-2 py-1.5 font-medium text-muted-foreground">Name</th>
            <th className="text-right px-2 py-1.5 font-medium text-muted-foreground w-20">
              Amount
            </th>
            <th className="text-left px-2 py-1.5 font-medium text-muted-foreground w-14">Unit</th>
            {showSource && (
              <th className="text-left px-2 py-1.5 font-medium text-muted-foreground w-8"></th>
            )}
            {onEditExchange && <th className="w-6"></th>}
            {onDeleteExchange && <th className="w-6"></th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {exchanges.map((exchange) => (
            <tr
              key={exchange.id}
              onClick={() => handleExchangeClick(exchange)}
              className={cn(
                'cursor-pointer hover:bg-accent/50 transition-colors group/row',
                selectedNodeId === exchange.id && 'bg-accent',
              )}
            >
              <td className="px-2 py-1.5 truncate max-w-[180px]" title={exchange.name}>
                {exchange.name}
              </td>
              <td className="px-2 py-1.5 text-right font-mono">{formatAmount(exchange.amount)}</td>
              <td className="px-2 py-1.5 text-muted-foreground">{exchange.unit}</td>
              {showSource && (
                <td className="px-2 py-1.5">
                  {exchange.input_database &&
                    !exchange.input_database.startsWith('project:') &&
                    exchange.input_database !== 'custom' && (
                      <span title={exchange.input_database}>
                        <Database className="h-3 w-3 text-muted-foreground" />
                      </span>
                    )}
                </td>
              )}
              {onEditExchange && (
                <td className="px-0.5 py-1.5">
                  <button
                    className="p-0.5 rounded opacity-40 group-hover/row:opacity-100 group-focus-within/row:opacity-100 focus-visible:opacity-100 hover:bg-accent hover:text-foreground transition-all"
                    onClick={(e) => {
                      e.stopPropagation()
                      setEditing(exchange)
                    }}
                    title="Edit exchange"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                </td>
              )}
              {onDeleteExchange && (
                <td className="px-1 py-1.5">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        className="p-0.5 rounded opacity-40 group-hover/row:opacity-100 group-focus-within/row:opacity-100 focus-visible:opacity-100 hover:bg-destructive/10 hover:text-destructive transition-all"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-sm">Delete Exchange</AlertDialogTitle>
                        <AlertDialogDescription className="text-xs">
                          Delete "{exchange.name}"? This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="h-8 text-xs">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="h-8 text-xs bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={() => onDeleteExchange(exchange.id, exchange.name)}
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {editing && onEditExchange && (
        <EditExchangeDialog
          exchange={editing}
          onClose={() => setEditing(null)}
          onSave={async (patch) => {
            await onEditExchange(editing.id, patch)
            setEditing(null)
          }}
        />
      )}
    </div>
  )
}

// Edit Exchange Dialog — name editable only on custom exchanges; amount always.
interface EditExchangeDialogProps {
  exchange: ProjectExchange
  onClose: () => void
  onSave: (patch: { amount?: number; name?: string }) => Promise<void>
}

function EditExchangeDialog({ exchange, onClose, onSave }: EditExchangeDialogProps) {
  const isCustomSource =
    exchange.input_database === 'custom' ||
    exchange.input_database?.startsWith('project:') ||
    !exchange.input_database
  const nameEditable = isCustomSource

  const [name, setName] = useState(exchange.name)
  const [amount, setAmount] = useState(String(exchange.amount))
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    const numAmount = parseFloat(amount)
    if (isNaN(numAmount)) {
      toast.error('Amount must be a number')
      return
    }
    const patch: { amount?: number; name?: string } = {}
    if (numAmount !== exchange.amount) patch.amount = numAmount
    if (nameEditable && name !== exchange.name) patch.name = name
    if (Object.keys(patch).length === 0) {
      onClose()
      return
    }
    setSaving(true)
    try {
      await onSave(patch)
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Failed to update exchange')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o && !saving) onClose()
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm">Edit exchange</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="edit-name" className="text-xs">
              Name
            </Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!nameEditable || saving}
              className="h-8 text-xs mt-1"
            />
            {!nameEditable && (
              <p className="text-[10px] text-muted-foreground mt-1">
                Name is fixed for database-linked exchanges.
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="edit-amount" className="text-xs">
              Amount ({exchange.unit})
            </Label>
            <Input
              id="edit-amount"
              type="number"
              step="any"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={saving}
              className="h-8 text-xs mt-1"
              autoFocus
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Empty State Component
function EmptyState({ message, onAdd }: { message: string; onAdd?: () => void }) {
  return (
    <div className="text-center py-3">
      <p className="text-xs text-muted-foreground">{message}</p>
      <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 mt-2" onClick={onAdd}>
        <Plus className="h-2.5 w-2.5" />
        Add
      </Button>
    </div>
  )
}

// Format amount helper
function formatAmount(amount: number): string {
  if (Math.abs(amount) >= 1000) {
    return amount.toLocaleString(undefined, { maximumFractionDigits: 1 })
  }
  if (Math.abs(amount) < 0.01 && amount !== 0) {
    return amount.toExponential(2)
  }
  return amount.toLocaleString(undefined, { maximumFractionDigits: 4 })
}
